import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api, ApiError, assistantChat, SETTINGS_UPDATED_EVENT, type AssistantMessage, type AssistantProposal, type AssistantSource, type ChartSpec } from '../../api/client';
import { useAuth } from '../../auth';
import { useI18n, type MessageKey } from '../../i18n';

export type ProposalItem = { kind: 'proposal'; proposal: AssistantProposal; status: 'open' | 'applying' | 'applied' | 'dismissed'; error?: string };
export type ChartItem = { kind: 'chart'; chart: ChartSpec };
/** An answer, with what it was computed from and the follow-up questions the assistant suggested. */
export type AssistantItem = { kind: 'assistant'; text: string; sources?: AssistantSource[]; suggestions?: string[] };
export type Item = { kind: 'user' | 'error'; text: string } | AssistantItem | ProposalItem | ChartItem;

export interface Chat {
  id: string;
  title: string;
  items: Item[];
  updatedAt: number;
}

interface AssistantState {
  /** The user's role may use the assistant and an administrator hasn't turned it off (it may still not be configured). */
  allowed: boolean;
  /** The server has a model configured and the user's role may use it. */
  enabled: boolean;
  /** An administrator turned the assistant off on the Settings page. */
  off: boolean;
  /** The server runs the rule-based demo instead of a real model. */
  mock: boolean;
  /** Still asking the server whether the assistant is configured. */
  loading: boolean;
  /** Saved conversations, newest first. */
  chats: Chat[];
  active: Chat | null;
  /** What the assistant is doing right now, or null when idle. */
  status: string | null;
  ask: (text: string, surveyId?: string) => void;
  stop: () => void;
  newChat: () => void;
  openChat: (id: string) => void;
  deleteChat: (id: string) => void;
  updateProposal: (chatId: string, index: number, patch: Partial<ProposalItem>) => void;
}

const AssistantContext = createContext<AssistantState | null>(null);

const TOOL_LABELS: Record<string, MessageKey> = {
  list_surveys: 'assistant.tool.list_surveys',
  get_survey: 'assistant.tool.get_survey',
  get_results: 'assistant.tool.get_results',
  list_variables: 'assistant.tool.list_variables',
  crosstab: 'assistant.tool.crosstab',
  response_trend: 'assistant.tool.response_trend',
  describe: 'assistant.tool.describe',
  compare_groups: 'assistant.tool.compare_groups',
  drivers: 'assistant.tool.drivers',
  data_quality: 'assistant.tool.data_quality',
  field_progress: 'assistant.tool.field_progress',
  compare_surveys: 'assistant.tool.compare_surveys',
  get_text_answers: 'assistant.tool.get_text_answers',
  propose_question_changes: 'assistant.tool.propose_question_changes',
  propose_new_survey: 'assistant.tool.propose_new_survey',
};

/** Previous turns sent with each question; older ones are dropped to keep requests small. */
const HISTORY_TURNS = 20;
/** Conversations kept in this browser. */
const MAX_CHATS = 30;

function loadChats(key: string): Chat[] {
  try {
    const raw = localStorage.getItem(key);
    const chats = raw ? (JSON.parse(raw) as Chat[]) : [];
    // An apply interrupted by a reload never finished.
    return chats.map((c) => ({
      ...c,
      items: c.items.map((it) => (it.kind === 'proposal' && it.status === 'applying' ? { ...it, status: 'open' as const } : it)),
    }));
  } catch {
    return [];
  }
}

function newId() {
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

/**
 * Holds the assistant conversations shared by the floating panel and the
 * Assistant page. Conversations are kept in this browser only (the server
 * keeps nothing) and are separate per signed-in user. Mount it with
 * key={user.id} so switching accounts starts from that user's conversations.
 */
export function AssistantProvider({ enabled: allowed, children }: { enabled: boolean; children: ReactNode }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const storageKey = `assistant.chats.${user?.id ?? 'anon'}`;
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [mock, setMock] = useState(false);
  const [off, setOff] = useState(false);
  const [chats, setChats] = useState<Chat[]>(() => loadChats(storageKey));
  // The open conversation is remembered for this tab, so a reload continues it.
  const [activeId, setActiveId] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem(`${storageKey}.active`);
    } catch {
      return null;
    }
  });
  const [status, setStatus] = useState<string | null>(null);
  const abort = useRef<AbortController | null>(null);
  const chatsRef = useRef(chats);
  chatsRef.current = chats;

  useEffect(() => {
    if (!allowed) return;
    const load = () =>
      api.config().then(
        (c) => {
          setConfigured(!!c.assistant);
          setOff(!!c.assistantOff);
          setMock(!!c.assistantMock);
        },
        () => setConfigured(false),
      );
    void load();
    window.addEventListener(SETTINGS_UPDATED_EVENT, load);
    return () => window.removeEventListener(SETTINGS_UPDATED_EVENT, load);
  }, [allowed]);
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(chats));
    } catch {
      // storage full or unavailable: conversations just won't survive a reload
    }
  }, [chats, storageKey]);
  useEffect(() => {
    try {
      if (activeId) sessionStorage.setItem(`${storageKey}.active`, activeId);
      else sessionStorage.removeItem(`${storageKey}.active`);
    } catch {
      // not remembered across reloads
    }
  }, [activeId, storageKey]);
  useEffect(() => () => abort.current?.abort(), []);

  const editChat = useCallback((id: string, fn: (items: Item[]) => Item[]) => {
    setChats((list) => list.map((c) => (c.id === id ? { ...c, items: fn(c.items), updatedAt: Date.now() } : c)));
  }, []);

  const ask = useCallback(
    (raw: string, surveyId?: string) => {
      const text = raw.trim();
      if (!text || abort.current) return;
      let chat = chatsRef.current.find((c) => c.id === activeId);
      if (!chat) {
        chat = { id: newId(), title: text.slice(0, 80), items: [], updatedAt: Date.now() };
        const fresh = chat;
        setChats((list) => [fresh, ...list].slice(0, MAX_CHATS));
        setActiveId(fresh.id);
      }
      const chatId = chat.id;
      const history: AssistantMessage[] = [...chat.items, { kind: 'user' as const, text }]
        .filter((it): it is { kind: 'user'; text: string } | AssistantItem => it.kind === 'user' || it.kind === 'assistant')
        .slice(-HISTORY_TURNS)
        .map((it) => ({ role: it.kind, content: it.text }));
      // The server needs the history to start with a question.
      while (history[0]?.role === 'assistant') history.shift();
      editChat(chatId, (items) => [...items, { kind: 'user', text }]);
      setStatus(t('assistant.working'));
      const sources: AssistantSource[] = [];
      const ctrl = new AbortController();
      abort.current = ctrl;
      assistantChat(
        { messages: history, surveyId },
        (e) => {
          if (e.type === 'tool') setStatus(t(TOOL_LABELS[e.tool] ?? 'assistant.working'));
          else if (e.type === 'proposal') editChat(chatId, (items) => [...items, { kind: 'proposal', proposal: e.data, status: 'open' }]);
          else if (e.type === 'chart') editChat(chatId, (items) => [...items, { kind: 'chart', chart: e.data }]);
          else if (e.type === 'source') {
            const same = (a: AssistantSource) => a.surveyId === e.data.surveyId && a.detail === e.data.detail && a.filters === e.data.filters;
            if (!sources.some(same)) sources.push(e.data);
          } else if (e.type === 'suggestions')
            editChat(chatId, (items) => {
              const at = items.reduce((last, it, i) => (it.kind === 'assistant' ? i : last), -1);
              return items.map((it, i) => (i === at && it.kind === 'assistant' ? { ...it, suggestions: e.data.items } : it));
            });
          else if (e.type === 'message')
            editChat(chatId, (items) => [...items, { kind: 'assistant', text: e.text || '…', sources: sources.length ? [...sources] : undefined }]);
          else if (e.type === 'error') editChat(chatId, (items) => [...items, { kind: 'error', text: e.text }]);
        },
        ctrl.signal,
      )
        .catch((e) => {
          if (!ctrl.signal.aborted) {
            editChat(chatId, (items) => [...items, { kind: 'error', text: e instanceof ApiError ? e.message : t('assistant.failed') }]);
          }
        })
        .finally(() => {
          if (abort.current === ctrl) abort.current = null;
          setStatus(null);
        });
    },
    [activeId, editChat, t],
  );

  const value = useMemo<AssistantState>(() => {
    const sorted = [...chats].sort((a, b) => b.updatedAt - a.updatedAt);
    return {
      allowed: allowed && !off,
      off,
      enabled: allowed && configured === true,
      loading: allowed && configured === null,
      mock,
      chats: sorted,
      active: chats.find((c) => c.id === activeId) ?? null,
      status,
      ask,
      stop: () => abort.current?.abort(),
      newChat: () => {
        abort.current?.abort();
        setActiveId(null);
      },
      openChat: (id) => {
        abort.current?.abort();
        setActiveId(id);
      },
      deleteChat: (id) => {
        if (id === activeId) {
          abort.current?.abort();
          setActiveId(null);
        }
        setChats((list) => list.filter((c) => c.id !== id));
      },
      updateProposal: (chatId, index, patch) =>
        editChat(chatId, (items) => items.map((it, i) => (i === index && it.kind === 'proposal' ? { ...it, ...patch } : it))),
    };
  }, [allowed, off, configured, mock, chats, activeId, status, ask, editChat]);

  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>;
}

export function useAssistant(): AssistantState {
  const ctx = useContext(AssistantContext);
  if (!ctx) throw new Error('useAssistant outside AssistantProvider');
  return ctx;
}
